package com.fantasytenis.shared.domain.survivor.usecases

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.survivor.api.SurvivorApi
import com.fantasytenis.shared.data.survivor.dto.AdminDeleteSurvivorEntryRequest
import com.fantasytenis.shared.data.survivor.dto.AdminDeleteSurvivorEntryResponse
import com.fantasytenis.shared.data.survivor.dto.SetSurvivorLockOverrideRequest
import com.fantasytenis.shared.data.survivor.repository.SurvivorRepository
import com.fantasytenis.shared.data.survivor.repository.SurvivorLeaderboardResult
import com.fantasytenis.shared.domain.bracket.entity.LockOverride
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import com.fantasytenis.shared.domain.survivor.entity.*

// ===== SURVIVOR CONTEST USE CASES =====

/**
 * Get a survivor contest by ID.
 */
class GetSurvivorContestUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(contestId: String): Result<SurvivorContestEntity> {
        return survivorRepository.getSurvivorContest(contestId)
    }
}

/**
 * List available survivor contests with optional filters.
 */
class ListSurvivorContestsUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(
        status: Int? = null,
        tournamentId: String? = null,
        featuredOnly: Boolean = false,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<SurvivorContestEntity>> {
        return survivorRepository.listSurvivorContests(status, tournamentId, featuredOnly, limit, offset)
    }
}

// ===== SURVIVOR ENTRY USE CASES =====

/**
 * Enter a survivor contest.
 */
class EnterSurvivorContestUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(contestId: String): Result<SurvivorEntryEntity> {
        return survivorRepository.createSurvivorEntry(contestId)
    }
}

/**
 * Get a survivor entry with all its picks.
 */
class GetSurvivorEntryUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(entryId: String): Result<SurvivorEntryWithPicksEntity> {
        return survivorRepository.getSurvivorEntry(entryId)
    }
}

/**
 * Get current user's survivor entries.
 */
class GetUserSurvivorEntriesUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(contestId: String? = null): Result<List<SurvivorEntryEntity>> {
        return survivorRepository.getUserSurvivorEntries(contestId)
    }
}

/**
 * Rebuy into a survivor contest after elimination.
 */
class RebuySurvivorEntryUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(entryId: String): Result<SurvivorEntryEntity> {
        return survivorRepository.rebuySurvivorEntry(entryId)
    }
}

/**
 * Cancel/withdraw from a survivor contest.
 */
class CancelSurvivorEntryUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(entryId: String): Result<SurvivorEntryEntity> {
        return survivorRepository.cancelSurvivorEntry(entryId)
    }
}

// ===== SURVIVOR PICK USE CASES =====

/**
 * Get available players to pick for the current round.
 */
class GetAvailablePlayersUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(
        entryId: String,
        round: Int? = null
    ): Result<List<AvailablePlayerEntity>> {
        return survivorRepository.getAvailablePlayers(entryId, round)
    }
}

/**
 * Make a survivor pick for the current round.
 */
class MakeSurvivorPickUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(
        entryId: String,
        playerId: String
    ): Result<SurvivorPickEntity> {
        return survivorRepository.makeSurvivorPick(entryId, playerId)
    }
}

/**
 * Update a survivor pick before the match starts.
 */
class UpdateSurvivorPickUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(
        entryId: String,
        pickId: String,
        playerId: String
    ): Result<SurvivorPickEntity> {
        return survivorRepository.updateSurvivorPick(entryId, pickId, playerId)
    }
}

// ===== LEADERBOARD USE CASES =====

/**
 * Get survivor contest leaderboard.
 */
class GetSurvivorLeaderboardUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(
        contestId: String,
        statusFilter: String? = null,
        limit: Int = 100,
        offset: Int = 0
    ): Result<SurvivorLeaderboardResult> {
        return survivorRepository.getSurvivorLeaderboard(contestId, statusFilter, limit, offset)
    }
}

/**
 * Get survivor contest round summaries.
 */
class GetSurvivorRoundSummaryUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(contestId: String): Result<List<SurvivorRoundSummaryEntity>> {
        return survivorRepository.getSurvivorRoundSummary(contestId)
    }
}

// ===== COMPOSITE USE CASES =====

/**
 * Get full survivor contest data including entry and available players.
 */
class GetSurvivorContestDetailUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(
        contestId: String,
        entryId: String? = null
    ): Result<SurvivorContestDetailData> {
        // Get contest details
        val contestResult = survivorRepository.getSurvivorContest(contestId)
        val contest = contestResult.getOrElse { return Result.failure(it) }

        // Get round summaries
        val roundSummaryResult = survivorRepository.getSurvivorRoundSummary(contestId)
        val roundSummaries = roundSummaryResult.getOrElse { emptyList() }

        // Get user's entry if provided
        var entryWithPicks: SurvivorEntryWithPicksEntity? = null
        var availablePlayers: List<AvailablePlayerEntity> = emptyList()

        if (entryId != null) {
            val entryResult = survivorRepository.getSurvivorEntry(entryId)
            entryWithPicks = entryResult.getOrNull()

            // Get available players for picking
            val playersResult = survivorRepository.getAvailablePlayers(entryId)
            availablePlayers = playersResult.getOrElse { emptyList() }
        }

        return Result.success(
            SurvivorContestDetailData(
                contest = contest,
                roundSummaries = roundSummaries,
                userEntry = entryWithPicks,
                availablePlayers = availablePlayers
            )
        )
    }
}

/**
 * Complete survivor contest detail data.
 */
data class SurvivorContestDetailData(
    val contest: SurvivorContestEntity,
    val roundSummaries: List<SurvivorRoundSummaryEntity>,
    val userEntry: SurvivorEntryWithPicksEntity?,
    val availablePlayers: List<AvailablePlayerEntity>
) {
    val hasEntry: Boolean get() = userEntry != null
    val isAlive: Boolean get() = userEntry?.entry?.isAlive == true
    val currentRoundSummary: SurvivorRoundSummaryEntity?
        get() = contest.currentRound?.let { round ->
            roundSummaries.find { it.round == round }
        }
    val canMakePick: Boolean
        get() = isAlive && (currentRoundSummary?.isPickingOpen == true)
}

/**
 * Get user's dashboard with all their active survivor entries.
 */
class GetUserSurvivorDashboardUseCase(
    private val survivorRepository: SurvivorRepository
) {
    suspend operator fun invoke(): Result<UserSurvivorDashboard> {
        // Get all user's entries
        val entriesResult = survivorRepository.getUserSurvivorEntries()
        val entries = entriesResult.getOrElse { return Result.failure(it) }

        // Separate by status
        val aliveEntries = entries.filter { it.isAlive }
        val eliminatedEntries = entries.filter { !it.isAlive && it.status != SurvivorEntryStatus.WITHDRAWN }
        val winnerEntries = entries.filter { it.status == SurvivorEntryStatus.WINNER || it.status == SurvivorEntryStatus.PAID }

        return Result.success(
            UserSurvivorDashboard(
                allEntries = entries,
                aliveEntries = aliveEntries,
                eliminatedEntries = eliminatedEntries,
                winnerEntries = winnerEntries,
                totalEntries = entries.size,
                aliveCount = aliveEntries.size
            )
        )
    }
}

/**
 * User's survivor dashboard data.
 */
data class UserSurvivorDashboard(
    val allEntries: List<SurvivorEntryEntity>,
    val aliveEntries: List<SurvivorEntryEntity>,
    val eliminatedEntries: List<SurvivorEntryEntity>,
    val winnerEntries: List<SurvivorEntryEntity>,
    val totalEntries: Int,
    val aliveCount: Int
) {
    val hasActiveEntries: Boolean get() = aliveEntries.isNotEmpty()
    val hasWins: Boolean get() = winnerEntries.isNotEmpty()
}

// ===== ADMIN USE CASES =====

/**
 * Set lock override for a survivor contest (SuperAdmin only).
 * Allows admins to manually unlock/lock contests regardless of round deadline.
 */
class SetSurvivorLockOverrideUseCase(
    private val survivorApi: SurvivorApi
) {
    /**
     * @param contestId The ID of the survivor contest
     * @param lockOverride The lock override value (NONE, FORCE_UNLOCKED, FORCE_LOCKED)
     * @param reason Optional reason for the override
     * @return The updated SurvivorContestEntity
     */
    suspend operator fun invoke(
        contestId: String,
        lockOverride: LockOverride,
        reason: String? = null
    ): Result<SurvivorContestEntity> {
        val apiValue = when (lockOverride) {
            LockOverride.NONE -> 1
            LockOverride.FORCE_UNLOCKED -> 2
            LockOverride.FORCE_LOCKED -> 3
        }

        val request = SetSurvivorLockOverrideRequest(
            lockOverride = apiValue,
            reason = reason
        )

        return when (val response = survivorApi.setLockOverride(contestId, request)) {
            is ApiResponse.Success -> {
                val dto = response.data
                Result.success(
                    SurvivorContestEntity(
                        id = dto.id,
                        name = dto.name,
                        description = dto.description,
                        drawId = dto.drawId,
                        tournamentName = dto.tournamentName,
                        variation = SurvivorVariation.fromApiValue(dto.variation),
                        entryFee = dto.entryFee,
                        prizePool = dto.prizePool,
                        maxEntries = dto.maxEntries,
                        currentEntries = dto.currentEntries,
                        aliveEntries = dto.aliveEntries,
                        maxEntriesPerUser = dto.maxEntriesPerUser,
                        currentRound = dto.currentRound?.let { RoundName.fromApiValue(it) },
                        roundDeadline = dto.roundDeadline,
                        status = SurvivorContestStatus.fromApiValue(dto.status),
                        strikeLimit = dto.strikeLimit,
                        rebuyDeadlineRound = dto.rebuyDeadlineRound?.let { RoundName.fromApiValue(it) },
                        rebuyFee = dto.rebuyFee,
                        payoutStructure = emptyList(), // Parsing JSON not needed for admin response
                        isFeatured = dto.isFeatured,
                        createdAt = dto.createdAt,
                        lockOverride = LockOverride.fromApiValue(dto.lockOverride),
                        lockOverrideBy = dto.lockOverrideBy.ifBlank { null },
                        lockOverrideAt = dto.lockOverrideAt.ifBlank { null },
                        lockOverrideReason = dto.lockOverrideReason.ifBlank { null }
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

/**
 * Admin delete a survivor entry (SuperAdmin only).
 * This allows superadmins to remove any entry from a survivor contest.
 */
class AdminDeleteSurvivorEntryUseCase(
    private val survivorApi: SurvivorApi
) {
    /**
     * @param entryId The ID of the entry to delete
     * @param reason The reason for deletion (required for audit)
     * @return The admin delete response
     */
    suspend operator fun invoke(
        entryId: String,
        reason: String
    ): Result<AdminDeleteSurvivorEntryResponse> {
        val request = AdminDeleteSurvivorEntryRequest(reason = reason)

        return when (val response = survivorApi.adminDeleteSurvivorEntry(entryId, request)) {
            is ApiResponse.Success -> Result.success(response.data)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}
